<template>
  <div
    class="rounded-lg cursor-pointer"
    :class="
      isHoveringDelete ? 'border-2 border-red-500' : 'border-2 border-[#D7DFEA]'
    "
    :style="!props.isReadonly ? { backgroundColor: props.color } : {}">
    <slot v-if="hasSlot('format')" name="format">
      <PairingFunctionOptions
        :format="props.format"
        :color="props.color"
        :can-delete="props.canDelete"
        @update:format="handleFormatUpdate"
        @delete:item="handleDeleteItem"
        @delete:hover="handleHoverDelete"/>
    </slot>
    <div
      class="flex shrink items-center justify-start relative rounded-lg border-3 bg-white transition-all duration-300 h-[125px] w-[180px]"
      :class="
        props.format === PairingItemFormat.ImageText
          ? 'flex-col'
          : 'flex-col-reverse'
      "
      :style="{
        borderColor: !props.isReadonly || active ? props.color : '#ffffff',
      }">
      <div
        class="absolute top-1 right-1 h-4 w-4 rounded-full z-10"
        :style="
          props.isReadonly && active
            ? { backgroundColor: props.color, border: '2px solid white' }
            : {}
        ">
        <div
          class="absolute rounded-full w-[35%] h-[35%] top-[15%] right-[15%]"
          :class="props.isReadonly && active ? 'bg-white' : ''"></div>
      </div>
      <slot
        v-if="hasSlot('uploadImage') && format.includes('image')"
        name="uploadImage">
        <PairingUploadImage
          :id="props.id"
          v-model="imageUrl"
          :format="props.format"/>
      </slot>
      <slot
        v-if="hasSlot('headline') && format.includes('text')"
        name="headline"
        :title="props.title"
        :format="props.format">
        <HeadLine
          :key="maxRows + '-' + maxLength"
          v-model="title"
          v-tooltip.top="{
            value: format.includes('image') && title.length > 18 ? title : '',
            class: `!z-[1300] ${direction === 'ltr' ? 'text-left' : 'text-right'}`,
          }"
          :value="truncatedTitle"
          :closable="false"
          max-width="100%"
          :placeholder="'כאן כותבים טקסט'"
          :max-rows="maxRows"
          :max-length="maxLength"
          class="h-full my-1 text-[15px]"
          @change="handleTitleChange"/>
      </slot>
      <slot
        v-if="hasSlot('image') && format.includes('image')"
        name="image"
        :image="props.image"
        :format="props.format"></slot>
      <slot
        v-if="hasSlot('title') && format.includes('text')"
        name="title"
        :title="props.title"
        :format="props.format"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, type PropType, useSlots, computed, inject } from 'vue';
import { PairingItemFormat } from '@amit/types';
import { HeadLine } from '@amit/components';
import PairingFunctionOptions from '../../../../products/builder/src/blocks/Pairing/PairingFunctionOptions.vue';
import PairingUploadImage from '../../../../products/builder/src/blocks/Pairing/PairingUploadImage.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  pairIndex: {
    type: Number,
    required: false,
  },
  format: {
    type: String as PropType<PairingItemFormat>,
    default: PairingItemFormat.TextImage,
  },
  title: {
    type: String,
    default: '',
  },
  image: {
    type: String,
    default: '',
  },
  color: {
    type: String,
    default: '',
  },
  matches: {
    type: Array<String>,
    default: [''],
  },
  active: {
    type: Boolean,
    default: false,
  },
  isReadonly: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: String,
    default: '',
  },
  canDelete: {
    type: Boolean,
    default: true,
  },
  isHoveringDelete: {
    type: Boolean,
    default: false,
  },
});

const direction = inject('direction');
const imageUrl = defineModel<string>({ required: true });
const slots = useSlots();
const hasSlot = name => !!slots[name];

const emit = defineEmits<{
  'update:format': [format: PairingItemFormat];
  'update:title': [title: string];
  'delete:item': [id: string];
  'delete:hover': [hovering: boolean];
}>();

const handleFormatUpdate = (format: PairingItemFormat) => {
  emit('update:format', format);
};

const handleDeleteItem = () => {
  emit('delete:item', props.id);
};

const handleHoverDelete = (hovering: boolean) => {
  if (props.canDelete) {
    emit('delete:hover', hovering);
  }
};

const title = ref(props.title);
const truncatedTitle = computed(() => {
  if (props.format.includes('image') && title.value.length > 18) {
    return title.value.slice(0, 15) + '...';
  }
  return title.value;
});
watch(
  () => props.title,
  newValue => {
    title.value = newValue;
  },
);
const handleTitleChange = () => {
  if (title.value !== props.title) {
    emit('update:title', title.value);
  }
};

const maxRows = computed(() => (props.format.includes('image') ? 1 : 5));
const maxLength = computed(() => (props.format.includes('image') ? 18 : 95));
</script>
